import React, { useReducer, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import {
  createBoard,
  canPlace,
  placePieceOnBoard,
  findCompletedLines,
  clearLines,
  getClearedCells,
  canPlaceAnyPiece,
  applyBomb,
  countFilledCells,
  BOARD_SIZE,
} from '../game/engine';
import { generatePieces, seededRandom } from '../game/pieces';
import { calculateScore, getComboText } from '../game/scoring';
import { submitScore, submitDailyScore, getDailyChallenge } from '../services/api';
import { useEntitlements } from '../contexts/EntitlementContext';
import { useSession } from '../hooks/useSession';
import { useAdManager } from '../hooks/useAdManager';
import { useOnboarding } from '../hooks/useOnboarding';
import toast from 'react-hot-toast';
import PaymentService from '../services/payment';
import { PRODUCT_KEYS } from '../services/subscription';
import Board from '../components/Board';
import PowerUpBar from '../components/PowerUpBar';
import PieceTray from '../components/PieceTray';
import ScoreDisplay from '../components/ScoreDisplay';
import GameOver from '../components/GameOver';
import '../App.css';

const FINGER_OFFSET = 70;
const DRAG_THRESHOLD = 8;

function getInitialState(highScore = 0, rng = null) {
  const pieces = generatePieces(3, rng || undefined);
  return {
    board: createBoard(),
    pieces,
    score: 0,
    highScore,
    combo: 0,
    gameOver: false,
    clearedCells: [],
    lastPoints: 0,
    lastCombo: 0,
    linesCleared: 0,
    moveCount: 0,
    rng,
    powerUps: { undo: 0, bomb: 0, refresh: 0 },
    powerUpMode: null,
    previousState: null,
  };
}

function gameReducer(state, action) {
  switch (action.type) {
    case 'PLACE_PIECE': {
      const { pieceIndex, row, col } = action;
      const piece = state.pieces[pieceIndex];
      if (!piece) return state;
      if (!canPlace(state.board, piece, row, col)) return state;

      const previousState = {
        board: state.board,
        pieces: state.pieces,
        score: state.score,
        combo: state.combo,
      };

      const boardWithPiece = placePieceOnBoard(state.board, piece, row, col);
      const lines = findCompletedLines(boardWithPiece);
      const lineCount = lines.rows.length + lines.cols.length;
      const cleared = getClearedCells(boardWithPiece, lines);
      const newBoard = clearLines(boardWithPiece, lines);

      const blocksCleared = cleared.length;
      const newCombo = lineCount > 0 ? state.combo + 1 : 0;
      const points = calculateScore(blocksCleared, newCombo);
      const newScore = state.score + points;

      const remainingPieces = state.pieces.filter((_, i) => i !== pieceIndex);
      const finalPieces = remainingPieces.length === 0
        ? generatePieces(3, state.rng || undefined)
        : remainingPieces;
      const gameOver = !canPlaceAnyPiece(newBoard, finalPieces);
      const newHighScore = Math.max(state.highScore, newScore);

      return {
        ...state,
        board: newBoard,
        pieces: finalPieces,
        score: newScore,
        highScore: newHighScore,
        combo: newCombo,
        gameOver,
        clearedCells: cleared,
        lastPoints: points,
        lastCombo: newCombo,
        linesCleared: state.linesCleared + lineCount,
        moveCount: state.moveCount + 1,
        previousState,
      };
    }

    case 'CLEAR_ANIMATION':
      return { ...state, clearedCells: [], lastPoints: 0 };

    case 'NEW_GAME':
      return getInitialState(state.highScore, action.rng || null);

    case 'USE_UNDO': {
      if (!state.previousState || state.powerUps.undo <= 0) return state;
      return {
        ...state,
        board: state.previousState.board,
        pieces: state.previousState.pieces,
        score: state.previousState.score,
        combo: state.previousState.combo,
        gameOver: false,
        previousState: null,
        powerUps: { ...state.powerUps, undo: state.powerUps.undo - 1 },
        powerUpMode: null,
      };
    }

    case 'ACTIVATE_BOMB': {
      if (state.powerUps.bomb <= 0 || state.gameOver) return state;
      return { ...state, powerUpMode: state.powerUpMode === 'bomb' ? null : 'bomb' };
    }

    case 'APPLY_BOMB': {
      if (state.powerUps.bomb <= 0 || state.gameOver) return state;
      const { row, col } = action;
      const bombedBoard = applyBomb(state.board, row, col);
      const bombLines = findCompletedLines(bombedBoard);
      const bombCleared = getClearedCells(bombedBoard, bombLines);
      const boardAfterBomb = clearLines(bombedBoard, bombLines);
      const bombLineCount = bombLines.rows.length + bombLines.cols.length;
      const bombCombo = bombLineCount > 0 ? state.combo + 1 : state.combo;
      const bombPoints = bombLineCount > 0 ? calculateScore(bombCleared.length, bombCombo) : 0;
      return {
        ...state,
        board: boardAfterBomb,
        score: state.score + bombPoints,
        highScore: Math.max(state.highScore, state.score + bombPoints),
        combo: bombCombo,
        clearedCells: bombCleared,
        lastPoints: bombPoints,
        lastCombo: bombCombo,
        powerUps: { ...state.powerUps, bomb: state.powerUps.bomb - 1 },
        powerUpMode: null,
      };
    }

    case 'USE_REFRESH': {
      if (state.powerUps.refresh <= 0 || state.gameOver) return state;
      const newPieces = generatePieces(3, state.rng || undefined);
      const refreshGameOver = !canPlaceAnyPiece(state.board, newPieces);
      return {
        ...state,
        pieces: newPieces,
        gameOver: refreshGameOver,
        powerUps: { ...state.powerUps, refresh: state.powerUps.refresh - 1 },
        powerUpMode: null,
      };
    }

    case 'SET_POWER_UPS':
      return { ...state, powerUps: { ...state.powerUps, ...action.powerUps } };

    case 'ADD_POWER_UP':
      return {
        ...state,
        powerUps: {
          ...state.powerUps,
          [action.powerUpType]: state.powerUps[action.powerUpType] + (action.count || 1),
        },
      };

    default:
      return state;
  }
}

export default function PlayScreen() {
  const navigate = useNavigate();
  const { mode } = useParams();
  const gameMode = mode === 'daily' ? 'daily' : 'classic';

  const savedHighScore = useRef(parseInt(localStorage.getItem('blockBlastHighScore') || '0', 10));
  const [user, setUser] = useState(null);
  const [game, dispatch] = useReducer(gameReducer, getInitialState(savedHighScore.current));
  const [dailySeed, setDailySeed] = useState(null);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [ghost, setGhost] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPiece, setDragPiece] = useState(null);
  const [dragValid, setDragValid] = useState(true);
  const [shaking, setShaking] = useState(false);
  const [scorePopup, setScorePopup] = useState(null);

  const { sessionNumber, gameOverCount, recordGameOver } = useSession();
  const { onGameOver: onGameOverAd, showRewardedForPowerUp } = useAdManager(sessionNumber, gameOverCount);
  useOnboarding(sessionNumber, dispatch, user);
  const { isPremium } = useEntitlements();

  const boardRef = useRef(null);
  const floatingRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const pointerRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const dragActiveRef = useRef(false);
  const dragPieceRef = useRef(null);
  const ghostRef = useRef(null);
  const dragPieceIndexRef = useRef(-1);

  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then((u) => { if (!cancelled) setUser(u); })
      .catch(() => { /* guest */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (gameMode === 'daily') {
      getDailyChallenge()
        .then((data) => {
          if (cancelled) return;
          const seed = data?.seed ?? generateFallbackSeed();
          const rng = seededRandom(seed);
          setDailySeed(seed);
          dispatch({ type: 'NEW_GAME', rng });
        })
        .catch(() => {
          if (cancelled) return;
          const seed = generateFallbackSeed();
          const rng = seededRandom(seed);
          setDailySeed(seed);
          dispatch({ type: 'NEW_GAME', rng });
        });
    } else {
      dispatch({ type: 'NEW_GAME' });
    }
    return () => { cancelled = true; };
  }, [gameMode]);

  useEffect(() => {
    localStorage.setItem('blockBlastHighScore', game.highScore.toString());
  }, [game.highScore]);

  useEffect(() => {
    if (!game.gameOver || !user) return;
    const payload = {
      score: game.score,
      linesCleared: game.linesCleared,
      bestCombo: game.combo,
      moves: game.moveCount,
      duration: 0,
    };
    if (gameMode === 'daily') submitDailyScore(payload).catch(() => { /* surfaced by api client */ });
    else submitScore(payload).catch(() => { /* surfaced by api client */ });
    recordGameOver();
    onGameOverAd();
  }, [game.gameOver, game.score, game.linesCleared, game.combo, game.moveCount, user, gameMode, recordGameOver, onGameOverAd]);

  useEffect(() => {
    if (game.clearedCells.length === 0) return;
    if (game.lastCombo > 0) {
      setShaking(true);
      const t = setTimeout(() => setShaking(false), 400);
      setScorePopup({ points: game.lastPoints, combo: game.lastCombo, id: Date.now() });
      const t2 = setTimeout(() => setScorePopup(null), 1200);
      const t3 = setTimeout(() => dispatch({ type: 'CLEAR_ANIMATION' }), 500);
      return () => { clearTimeout(t); clearTimeout(t2); clearTimeout(t3); };
    }
    if (game.lastPoints > 0) {
      setScorePopup({ points: game.lastPoints, combo: 0, id: Date.now() });
      const t2 = setTimeout(() => setScorePopup(null), 1200);
      const t3 = setTimeout(() => dispatch({ type: 'CLEAR_ANIMATION' }), 500);
      return () => { clearTimeout(t2); clearTimeout(t3); };
    }
    const t = setTimeout(() => dispatch({ type: 'CLEAR_ANIMATION' }), 500);
    return () => clearTimeout(t);
  }, [game.clearedCells, game.lastCombo, game.lastPoints]);

  const vibrate = useCallback((pattern) => {
    if (navigator.vibrate) navigator.vibrate(pattern);
  }, []);

  const calcGhost = useCallback((px, py, piece) => {
    const el = boardRef.current;
    if (!el || !piece) return null;
    const rect = el.getBoundingClientRect();
    const cellSize = rect.width / BOARD_SIZE;
    const relX = px - rect.left;
    const relY = py - rect.top - FINGER_OFFSET;
    const pRows = piece.shape.length;
    const pCols = piece.shape[0].length;
    const col = Math.round(relX / cellSize - pCols / 2);
    const row = Math.round(relY / cellSize - pRows / 2);
    const cr = Math.max(0, Math.min(row, BOARD_SIZE - pRows));
    const cc = Math.max(0, Math.min(col, BOARD_SIZE - pCols));
    if (relX < -cellSize * 2 || relX > rect.width + cellSize * 2 || relY < -cellSize * 2 || relY > rect.height + cellSize * 2) return null;
    return { row: cr, col: cc, valid: canPlace(game.board, piece, cr, cc), piece };
  }, [game.board]);

  const updateDrag = useCallback(() => {
    if (!dragActiveRef.current) return;
    const el = floatingRef.current;
    const piece = dragPieceRef.current;
    const boardEl = boardRef.current;
    if (el && piece && boardEl) {
      const rect = boardEl.getBoundingClientRect();
      const cellSize = rect.width / BOARD_SIZE;
      const pieceWidth = piece.shape[0].length * cellSize - 2;
      const pieceHeight = piece.shape.length * cellSize - 2;
      const x = pointerRef.current.x - pieceWidth / 2;
      const y = pointerRef.current.y - FINGER_OFFSET - pieceHeight / 2;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    }
    if (piece) {
      const newGhost = calcGhost(pointerRef.current.x, pointerRef.current.y, piece);
      const prev = ghostRef.current;
      if ((!prev && newGhost) || (prev && !newGhost) || (prev && newGhost && (prev.row !== newGhost.row || prev.col !== newGhost.col || prev.valid !== newGhost.valid))) {
        ghostRef.current = newGhost;
        const nowValid = !newGhost || newGhost.valid;
        setDragValid((cur) => (cur === nowValid ? cur : nowValid));
      }
    }
    rafRef.current = requestAnimationFrame(updateDrag);
  }, [calcGhost]);

  const handlePiecePointerDown = useCallback((e, pieceIndex) => {
    if (game.gameOver) return;
    if (e.type === 'keydown') {
      setSelectedPiece((prev) => (prev === pieceIndex ? null : pieceIndex));
      setGhost(null);
      ghostRef.current = null;
      return;
    }
    e.preventDefault();
    const piece = game.pieces[pieceIndex];
    if (!piece) return;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    pointerRef.current = { x: e.clientX, y: e.clientY };
    dragPieceRef.current = piece;
    dragPieceIndexRef.current = pieceIndex;
    setDragPiece({ index: pieceIndex, piece });
    setDragValid(true);
    setIsDragging(true);
    dragActiveRef.current = true;
    rafRef.current = requestAnimationFrame(updateDrag);

    const handleMove = (ev) => { pointerRef.current = { x: ev.clientX, y: ev.clientY }; };
    const handleUp = (ev) => {
      dragActiveRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const dx = ev.clientX - dragStartRef.current.x;
      const dy = ev.clientY - dragStartRef.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < DRAG_THRESHOLD) {
        setSelectedPiece((prev) => (prev === pieceIndex ? null : pieceIndex));
      } else {
        const g = ghostRef.current;
        if (g && g.valid) {
          dispatch({ type: 'PLACE_PIECE', pieceIndex, row: g.row, col: g.col });
          vibrate(15);
          setSelectedPiece(null);
        }
      }
      setIsDragging(false);
      setDragPiece(null);
      setGhost(null);
      ghostRef.current = null;
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  }, [game.pieces, game.gameOver, updateDrag, vibrate]);

  const handleCellClick = useCallback((row, col) => {
    if (selectedPiece === null || game.gameOver) return;
    const piece = game.pieces[selectedPiece];
    if (!piece) return;
    const pRows = piece.shape.length;
    const pCols = piece.shape[0].length;
    const pr = Math.max(0, Math.min(row - Math.floor(pRows / 2), BOARD_SIZE - pRows));
    const pc = Math.max(0, Math.min(col - Math.floor(pCols / 2), BOARD_SIZE - pCols));
    if (canPlace(game.board, piece, pr, pc)) {
      dispatch({ type: 'PLACE_PIECE', pieceIndex: selectedPiece, row: pr, col: pc });
      vibrate(15);
      setSelectedPiece(null);
      setGhost(null);
      ghostRef.current = null;
    }
  }, [selectedPiece, game.pieces, game.board, game.gameOver, vibrate]);

  const handleCellHover = useCallback((row, col) => {
    if (selectedPiece === null || isDragging) return;
    const piece = game.pieces[selectedPiece];
    if (!piece) return;
    const pRows = piece.shape.length;
    const pCols = piece.shape[0].length;
    const pr = Math.max(0, Math.min(row - Math.floor(pRows / 2), BOARD_SIZE - pRows));
    const pc = Math.max(0, Math.min(col - Math.floor(pCols / 2), BOARD_SIZE - pCols));
    setGhost({ row: pr, col: pc, valid: canPlace(game.board, piece, pr, pc), piece });
  }, [selectedPiece, game.pieces, game.board, isDragging]);

  const handleBoardLeave = useCallback(() => {
    if (!isDragging) { setGhost(null); ghostRef.current = null; }
  }, [isDragging]);

  const handleNewGame = useCallback(() => {
    if (gameMode === 'daily' && dailySeed) {
      const rng = seededRandom(dailySeed);
      dispatch({ type: 'NEW_GAME', rng });
    } else {
      dispatch({ type: 'NEW_GAME' });
    }
    setSelectedPiece(null);
    setGhost(null);
    ghostRef.current = null;
    setShaking(false);
    setScorePopup(null);
  }, [gameMode, dailySeed]);

  const handlePurchaseStarterPack = useCallback(async () => {
    const result = await PaymentService.purchase(PRODUCT_KEYS.STARTER_PACK);
    if (!result?.success && !result?.canceled) {
      toast.error(result?.error || 'Purchase failed');
    }
  }, []);

  const handleWatchAd = useCallback(async () => {
    const completed = await showRewardedForPowerUp('bomb');
    if (completed) dispatch({ type: 'ADD_POWER_UP', powerUpType: 'bomb', count: 1 });
  }, [showRewardedForPowerUp]);

  const filledCells = game.gameOver ? countFilledCells(game.board) : 0;
  const showStarterPackOffer = !isPremium && sessionNumber >= 8 && game.score > game.highScore * 0.8;
  const showRewardedOffer = !isPremium && sessionNumber >= 4 && sessionNumber <= 7 && filledCells > 32;
  const rewardedPowerUpType = filledCells > 32 ? 'bomb' : 'undo';

  return (
    <div className="app" role="application" aria-label="Block Blast Game">
      <header className="app-header safe-area-top">
        <div className="game-top-bar">
          <button className="back-btn" onClick={() => navigate('/')} aria-label="Back to menu">←</button>
          <h1 className="app-title">{gameMode === 'daily' ? 'DAILY CHALLENGE' : 'BLOCK BLAST'}</h1>
          <div className="spacer" />
        </div>
      </header>

      <ScoreDisplay score={game.score} highScore={game.highScore} combo={game.combo} />

      <div className={`board-wrapper${shaking ? ' board-shake' : ''}`}>
        <Board
          ref={boardRef}
          board={game.board}
          ghost={ghost}
          clearedCells={game.clearedCells}
          onCellClick={handleCellClick}
          onCellHover={handleCellHover}
          onBoardLeave={handleBoardLeave}
          targetingMode={game.powerUpMode}
          onBombClick={(row, col) => { dispatch({ type: 'APPLY_BOMB', row, col }); vibrate([15, 50, 15]); }}
        />
        {scorePopup && (
          <div className="score-popup" key={scorePopup.id}>
            <span className="score-popup-points">+{scorePopup.points}</span>
            {scorePopup.combo > 1 && <span className="score-popup-combo">{getComboText(scorePopup.combo)}</span>}
          </div>
        )}
      </div>

      <PowerUpBar
        powerUps={game.powerUps}
        powerUpMode={game.powerUpMode}
        onUndo={() => { dispatch({ type: 'USE_UNDO' }); vibrate(15); }}
        onBomb={() => dispatch({ type: 'ACTIVATE_BOMB' })}
        onRefresh={() => { dispatch({ type: 'USE_REFRESH' }); vibrate(15); }}
        disabled={game.gameOver}
        canUndo={!!game.previousState}
      />

      <PieceTray
        pieces={game.pieces}
        selectedPiece={selectedPiece}
        onPiecePointerDown={handlePiecePointerDown}
        disabled={game.gameOver}
      />

      {isDragging && dragPiece && (
        <div className={`floating-piece${dragValid ? '' : ' floating-piece-invalid'}`} ref={floatingRef}>
          {dragPiece.piece.shape.map((row, r) => (
            <div key={r} className="floating-piece-row">
              {row.map((cell, c) => (
                <div
                  key={c}
                  className={`floating-piece-cell${cell ? ' filled' : ''}`}
                  style={cell ? { backgroundColor: dragPiece.piece.color, '--cell-color': dragPiece.piece.color } : {}}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {game.gameOver && (
        <GameOver
          score={game.score}
          highScore={game.highScore}
          linesCleared={game.linesCleared}
          moveCount={game.moveCount}
          onNewGame={handleNewGame}
          onBackToMenu={() => navigate('/')}
          isDaily={gameMode === 'daily'}
          sessionNumber={sessionNumber}
          onShowSubscription={() => navigate('/subscribe')}
          onPurchaseStarterPack={handlePurchaseStarterPack}
          onWatchAd={handleWatchAd}
          showStarterPackOffer={showStarterPackOffer}
          showRewardedOffer={showRewardedOffer}
          rewardedPowerUpType={rewardedPowerUpType}
        />
      )}
    </div>
  );
}

function generateFallbackSeed() {
  const today = new Date().toISOString().split('T')[0];
  let hash = 0;
  for (let i = 0; i < today.length; i++) {
    hash = ((hash << 5) - hash) + today.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
}
